import { t, tk } from "./i18n";
import { getLearnerId } from "./learnerId";
/** Thin fetch wrapper. Same-origin cookies; every non-2xx becomes an ApiError with the server's code. */
export class ApiError extends Error {
  constructor(public status: number, public code: string) {
    super(code);
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, ...rest } = init;
  const learner = path.startsWith("/api/learn/") ? getLearnerId() : null;
  const res = await fetch(path, {
    credentials: "same-origin",
    ...rest,
    headers: { ...(json !== undefined ? { "Content-Type": "application/json" } : {}), ...(learner ? { "X-Learner": learner } : {}), ...rest.headers },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new ApiError(res.status, data.error ?? "network_error");
  return data as T;
}

/** User-facing Malay messages for server error codes. */
const MESSAGES: Record<string, string> = {
  image_unavailable: tk("Gambar kertas belum dapat dimuat turun. Sila cuba muat turun semula."),
  validation_failed: tk("Sila semak maklumat yang dimasukkan."),
  invalid_email: tk("Emel tidak sah."),
  checkout_busy: tk("Terlalu ramai sedang membuat bayaran. Cuba lagi dalam beberapa minit."),
  order_not_found: tk("Pesanan tidak dijumpai. Semak kod rujukan dan emel."),
  order_not_pending: tk("Pesanan ini sudah diproses."),
  order_expired: tk("Tempoh pesanan telah tamat. Sila buat pesanan baharu."),
  rate_limited: tk("Terlalu banyak cubaan. Cuba lagi sebentar."),
  login_link_invalid: tk("Pautan log masuk tidak sah atau sudah tamat."),
  invitation_not_found: tk("Pautan pengaktifan tidak sah."),
  invitation_used: tk("Pautan pengaktifan sudah digunakan."),
  invitation_expired: tk("Pautan pengaktifan sudah tamat. Hubungi kami untuk pautan baharu."),
  email_mismatch: tk("Log masuk dengan emel yang digunakan semasa membeli."),
  email_not_verified: tk("Sahkan emel anda dahulu."),
  license_exists: tk("Akaun ini sudah mempunyai lesen."),
  order_not_paid: tk("Bayaran belum disahkan."),
  invalid_credentials: tk("Emel atau kata laluan salah. Belum ada kata laluan? Guna pautan emel."),
  too_many_attempts: tk("Terlalu banyak cubaan salah. Cuba lagi dalam 15 minit atau guna pautan emel."),
  unauthenticated: tk("Sila log masuk."),
  forbidden: tk("Anda tiada akses."),
  duplicate: tk("Rujukan bank ini sudah digunakan untuk pesanan lain."),
  early_bird_slot_lost: tk("Slot Early Bird pesanan ini telah dilepaskan."),
  network_error: tk("Ralat rangkaian. Cuba lagi."),
  quota_exceeded: tk("Had penjanaan dicapai. Kertas lama masih boleh dimuat turun."),
  credits_exhausted: tk("Kredit AI anda sudah habis. Tambah kredit di halaman Kredit untuk menjana lagi."),
  ai_budget_exhausted: tk("Penjana kertas sedang sibuk. Cuba lagi kemudian."),
  license_required: tk("Ciri ini untuk pemegang lesen."),
  too_many_learners: tk("Sudah mencapai had bilangan anak untuk satu akaun."),
  template_not_found: tk("Format kertas tidak dijumpai."),
};

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return t(MESSAGES[e.code] ?? tk("Ralat tidak dijangka. Cuba lagi."));
  return t(MESSAGES.network_error!);
}

/** Read `token` from the URL fragment (never sent to the server or logged). */
export function tokenFromHash(): string | null {
  const m = /(?:^|&)token=([^&]+)/.exec(window.location.hash.slice(1));
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

/** Poll a paper until it is ready or failed. Generation runs server-side, so leaving the page is safe. */
export async function waitForExam(id: string, timeoutMs = 14 * 60_000): Promise<{ status: string; failure?: string }> {
  const until = Date.now() + timeoutMs;
  while (Date.now() < until) {
    await new Promise((r) => setTimeout(r, 4000));
    try {
      const { exam } = await api<{ exam: { status: string; failure_reason: string | null } }>(`/api/exams/${id}`);
      if (exam.status === "ready" || exam.status === "failed") return { status: exam.status, ...(exam.failure_reason ? { failure: exam.failure_reason } : {}) };
    } catch {
      /* transient network error on mobile: keep polling */
    }
  }
  return { status: "generating" };
}

